import { CleaningServices, Publish } from '@mui/icons-material';
import { Menu as MuiMenu } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpgradeAllContentItems } from '~/api/content-items';
import { useCleanupTemplateVersions, useDeleteTemplate } from '~/api/templates';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { MoreAction, MoreDeleteAction } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { MoreActionProps, MoreMenuProps } from '~/hooks/table';
import type { TemplateLayout__Template as Template } from '../../queries/layout.generated';

export interface MenuProps {
  template: Template;
  moreMenuProps: MoreMenuProps<Template>;
  moreActionProps: MoreActionProps;
}

export const Menu = ({ template, moreMenuProps, moreActionProps }: MenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cleanup] = useCleanupTemplateVersions({ variables: { templateIds: template.id } });
  const [deleteTemplate] = useDeleteTemplate({ variables: { templateId: template.id } });
  const [upgradeAll, upgradeAllState] = useUpgradeAllContentItems({
    variables: { templateId: template.id },
  });

  // Separate confirm dialogs for better text
  const [confirmCleanup, confirmCleanupProps] = useConfirmDialog();
  const [confirmUpgrade, confirmUpgradeProps] = useConfirmDialog();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onCleanup = async () => {
    if (!(await confirmCleanup())) return;
    await cleanup();
  };

  const onDelete = async () => {
    if (!(await confirmDelete())) return;
    await deleteTemplate();
    // The menu renders inside a tab route, so the list is two levels up.
    navigate('../..', { replace: true });
  };

  const onUpgradeAll = async () => {
    if (!(await confirmUpgrade())) return;
    await upgradeAll();
    upgradeAllState.reset();
  };

  return (
    <>
      <MuiMenu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...moreMenuProps}
      >
        <MoreAction
          {...moreActionProps}
          Icon={Publish}
          disabled={!template.canUpgradeAll.value}
          onClick={() => void onUpgradeAll()}
          title={t('pages.templates.menu.upgrade_all')}
        />

        <MoreAction
          {...moreActionProps}
          Icon={CleaningServices}
          disabled={!template.canCleanup.value}
          onClick={() => void onCleanup()}
          title={t('pages.templates.menu.delete_unused_versions')}
        />

        <MoreDeleteAction
          {...moreActionProps}
          disabled={!template.canDestroy.value}
          tooltip={template.canDestroy.reasons?.fullMessages.join(', ') ?? ''}
          onClick={() => void onDelete()}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmUpgradeProps}
        title={t('pages.templates.menu.upgrade_confirm_title')}
        prompt={t('pages.templates.menu.upgrade_confirm_prompt')}
        confirm={t('pages.templates.menu.upgrade_confirm_button')}
      />

      <ConfirmDialog
        {...confirmCleanupProps}
        title={t('pages.templates.menu.cleanup_confirm_title')}
        prompt={t('pages.templates.menu.cleanup_confirm_prompt')}
        confirm={t('common.dialogs.confirm')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.templates.menu.delete_confirm_button')}
        deleteConfirm
        prompt={t('pages.templates.menu.delete_confirm_prompt')}
        title={t('pages.templates.menu.delete_confirm_title')}
      />
    </>
  );
};
